import React, { useState, useEffect, useCallback } from 'react';
import {
  Megaphone, User, AlertTriangle, Clock, Globe, CheckCircle,
  XCircle, Volume2, Loader2, RefreshCw, Radio, Plus,
  PlaneTakeoff, Zap, Play, Pencil, Send
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import { airportAnnouncementsAPI, announcementZonesAPI, gatesAPI } from '../../services/api';
import { useSupervisorSocket } from '../../hooks/useGateSocket';
import StatusBadge from '../../components/UI/StatusBadge';
import Button from '../../components/UI/Button';

const PRIORITY_CFG = {
  normal:    { label: 'Normal',    tone: 'neutral', dot: 'bg-gray-400' },
  urgent:    { label: 'Urgent',    tone: 'orange',  dot: 'bg-orange-500' },
  emergency: { label: 'Emergency', tone: 'danger',  dot: 'bg-red-600' },
};

// Same tones as OperatorApprovalsPage/OperatorAnnouncementsPage — same
// underlying AirportAnnouncement.status shown on a third page; 'broadcasting'
// was yet another one-off solid-blue badge here, now unified.
const STATUS_CFG = {
  pending:      { label: 'Pending',      tone: 'yellow' },
  generating:   { label: 'Generating',   tone: 'info' },
  ready:        { label: 'Ready',        tone: 'success' },
  broadcasting: { label: 'Broadcasting', tone: 'info', pulse: true },
  played:       { label: 'Played',       tone: 'neutral' },
  rejected:     { label: 'Rejected',     tone: 'danger' },
  cancelled:    { label: 'Cancelled',    tone: 'neutral' },
};

const PAGING_ACTIONS = [
  { value: 'proceed_to_gate',        label: 'Proceed to gate' },
  { value: 'contact_staff',          label: 'Contact airport staff' },
  { value: 'proceed_to_checkin',     label: 'Proceed to check-in' },
  { value: 'proceed_to_immigration', label: 'Proceed to immigration' },
  { value: 'other',                  label: 'Other (specify location)' },
];

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
}

// ── Create Announcement Modal ─────────────────────────────────────────────────

function CreateModal({ onClose, onCreated, zones, gates }) {
  const [annType, setAnnType] = useState('zone_announcement');
  const [submitting, setSubmitting] = useState(false);

  const [zoneForm, setZoneForm] = useState({ title: '', english_text: '', priority: 'normal' });
  const [pagingForm, setPagingForm] = useState({
    title: '', passenger_name: '', flight_number: '',
    paging_action: 'proceed_to_gate', action_location: '', urgency_reason: '', priority: 'urgent',
  });

  const [selectedZones, setSelectedZones] = useState([]);
  const [selectedGates, setSelectedGates] = useState([]);

  // Derive the "Airport Wide" zone (if any) and remaining zones
  const airportWideZone = zones.find(z =>
    z.zone_code === 'ALL' ||
    z.zone_name?.toLowerCase().replace(/\s/g, '') === 'airportwide'
  );
  const otherZones = zones.filter(z => z !== airportWideZone);

  // Group gates by first letter of gate_code (terminal)
  const gateGroups = gates.reduce((acc, g) => {
    const letter = g.gate_code?.charAt(0).toUpperCase() || '?';
    if (!acc[letter]) acc[letter] = [];
    acc[letter].push(g);
    return acc;
  }, {});
  const terminalLetters = Object.keys(gateGroups).sort();

  const isAirportWideSelected = airportWideZone && selectedZones.includes(airportWideZone.zone_code);
  const hasTargets = selectedZones.length > 0 || selectedGates.length > 0;
  const totalTargets = selectedZones.length + selectedGates.length;

  function toggleAirportWide() {
    if (!airportWideZone) return;
    if (isAirportWideSelected) {
      setSelectedZones([]);
      setSelectedGates([]);
    } else {
      setSelectedZones([airportWideZone.zone_code]);
      setSelectedGates([]);
    }
  }

  function toggleZone(code) {
    // Deselect airport wide when picking individual zones
    setSelectedZones(prev => {
      const withoutWide = airportWideZone
        ? prev.filter(z => z !== airportWideZone.zone_code)
        : prev;
      return withoutWide.includes(code)
        ? withoutWide.filter(z => z !== code)
        : [...withoutWide, code];
    });
  }

  function toggleGate(id) {
    const sid = String(id);
    setSelectedZones(prev => airportWideZone ? prev.filter(z => z !== airportWideZone.zone_code) : prev);
    setSelectedGates(prev => prev.includes(sid) ? prev.filter(g => g !== sid) : [...prev, sid]);
  }

  function toggleTerminal(letter) {
    const groupIds = gateGroups[letter].map(g => String(g.id));
    const allSelected = groupIds.every(id => selectedGates.includes(id));
    setSelectedZones(prev => airportWideZone ? prev.filter(z => z !== airportWideZone.zone_code) : prev);
    setSelectedGates(prev =>
      allSelected
        ? prev.filter(id => !groupIds.includes(id))
        : [...new Set([...prev, ...groupIds])]
    );
  }

  function buildPagingPreview() {
    const { passenger_name, flight_number, paging_action, action_location } = pagingForm;
    if (!passenger_name) return '';
    const actionMap = {
      proceed_to_gate: `proceed to ${action_location || '[gate]'} immediately`,
      contact_staff: `contact airport staff at ${action_location || '[location]'} immediately`,
      proceed_to_checkin: `proceed to the check-in counter${action_location ? ` at ${action_location}` : ''} immediately`,
      proceed_to_immigration: `proceed to immigration${action_location ? ` at ${action_location}` : ''} immediately`,
      other: action_location || '[location]',
    };
    const flightPart = flight_number ? ` traveling on flight ${flight_number}` : '';
    const action = actionMap[paging_action] || actionMap.other;
    return `Attention please. This is a passenger paging announcement. Would passenger ${passenger_name}${flightPart} please ${action}. We repeat, would passenger ${passenger_name}${flightPart} please ${action}. Thank you.`;
  }

  async function handleSubmit(broadcastNow) {
    if (broadcastNow && !hasTargets) {
      toast.error('Select at least one zone or gate to broadcast');
      return;
    }
    setSubmitting(true);
    try {
      let payload;
      if (annType === 'zone_announcement') {
        if (!zoneForm.title.trim() || !zoneForm.english_text.trim()) {
          toast.error('Title and message text are required'); return;
        }
        payload = { announcement_type: 'zone_announcement', title: zoneForm.title, english_text: zoneForm.english_text, priority: zoneForm.priority };
      } else {
        if (!pagingForm.passenger_name.trim()) {
          toast.error('Passenger name is required'); return;
        }
        payload = {
          announcement_type: 'passenger_paging',
          title: pagingForm.title || `Paging: ${pagingForm.passenger_name}`,
          passenger_name: pagingForm.passenger_name,
          flight_number: pagingForm.flight_number,
          paging_action: pagingForm.paging_action,
          action_location: pagingForm.action_location,
          urgency_reason: pagingForm.urgency_reason,
          priority: pagingForm.priority,
        };
      }

      const created = await airportAnnouncementsAPI.submit(payload);
      const annId = created.data.data.id;

      if (broadcastNow && hasTargets) {
        const targets = [
          ...selectedZones.map(code => {
            const z = zones.find(z => z.zone_code === code);
            return { target_type: 'zone', target_id: code, target_label: z?.zone_name || code };
          }),
          ...selectedGates.map(gid => {
            const g = gates.find(g => String(g.id) === gid);
            return { target_type: 'gate', target_id: gid, target_label: `Gate ${g?.gate_code || gid}` };
          }),
        ];
        await airportAnnouncementsAPI.approve(annId, { targets });
        toast.success('Approved and broadcast queued');
      } else {
        toast.success('Saved as pending — awaiting approval');
      }

      onCreated();
      onClose();
    } catch (err) {
      if (!err._toasted) toast.error('Failed to submit');
    } finally { setSubmitting(false); }
  }

  const inputCls = 'w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 focus:bg-white transition-colors';
  const selectCls = 'w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50';

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl flex flex-col max-h-[92vh]">

        {/* ── Sticky header ── */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="font-bold text-gray-900 text-lg">Create Announcement</h2>
            <p className="text-xs text-gray-400 mt-0.5">Fill in the details, then select where to broadcast</p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors">
            <XCircle size={20} />
          </button>
        </div>

        {/* ── Body: two-column ── */}
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-gray-100">

            {/* ── Left: announcement details ── */}
            <div className="px-6 py-5 space-y-5">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Announcement Details</p>

              {/* Type selector */}
              <div className="flex gap-2">
                {[
                  { val: 'zone_announcement', icon: <Globe size={14} />, label: 'Zone Announcement' },
                  { val: 'passenger_paging',  icon: <PlaneTakeoff size={14} />, label: 'Passenger Paging' },
                ].map(({ val, icon, label }) => (
                  <button key={val} type="button" onClick={() => setAnnType(val)}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-semibold border-2 transition-all ${
                      annType === val
                        ? 'border-blue-600 bg-blue-50 text-blue-700'
                        : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
                    }`}
                  >
                    {icon} {label}
                  </button>
                ))}
              </div>

              {/* Zone announcement fields */}
              {annType === 'zone_announcement' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <input type="text" value={zoneForm.title}
                      onChange={e => setZoneForm(f => ({ ...f, title: e.target.value }))}
                      placeholder="e.g. Gate Change Notice"
                      className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Message <span className="text-red-500">*</span>
                    </label>
                    <textarea value={zoneForm.english_text}
                      onChange={e => setZoneForm(f => ({ ...f, english_text: e.target.value }))}
                      rows={5} placeholder="Type the full announcement text here…"
                      className={`${inputCls} resize-none`} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Priority</label>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { val: 'normal',    label: 'Normal',    cls: 'border-gray-300 hover:border-gray-400 text-gray-700', active: 'border-blue-600 bg-blue-600 text-white' },
                        { val: 'urgent',    label: 'Urgent',    cls: 'border-orange-300 hover:border-orange-400 text-orange-700', active: 'border-orange-500 bg-orange-500 text-white' },
                        { val: 'emergency', label: 'Emergency', cls: 'border-red-300 hover:border-red-400 text-red-700', active: 'border-red-600 bg-red-600 text-white' },
                      ].map(({ val, label, cls, active }) => (
                        <button key={val} type="button"
                          onClick={() => setZoneForm(f => ({ ...f, priority: val }))}
                          className={`py-2.5 rounded-xl text-sm font-semibold border-2 transition-all ${zoneForm.priority === val ? active : `bg-white ${cls}`}`}
                        >{label}</button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Passenger paging fields */}
              {annType === 'passenger_paging' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Passenger Name <span className="text-red-500">*</span>
                    </label>
                    <input type="text" value={pagingForm.passenger_name}
                      onChange={e => setPagingForm(f => ({ ...f, passenger_name: e.target.value }))}
                      placeholder="Full name as on boarding pass"
                      className={inputCls} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Flight Number</label>
                      <input type="text" value={pagingForm.flight_number}
                        onChange={e => setPagingForm(f => ({ ...f, flight_number: e.target.value.toUpperCase() }))}
                        placeholder="e.g. SQ321" className={inputCls} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Priority</label>
                      <select value={pagingForm.priority}
                        onChange={e => setPagingForm(f => ({ ...f, priority: e.target.value }))}
                        className={selectCls}>
                        <option value="normal">Normal</option>
                        <option value="urgent">Urgent</option>
                        <option value="emergency">Emergency</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Action Required</label>
                    <select value={pagingForm.paging_action}
                      onChange={e => setPagingForm(f => ({ ...f, paging_action: e.target.value }))}
                      className={selectCls}>
                      {PAGING_ACTIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Location / Gate</label>
                    <input type="text" value={pagingForm.action_location}
                      onChange={e => setPagingForm(f => ({ ...f, action_location: e.target.value }))}
                      placeholder="e.g. Gate B12" className={inputCls} />
                  </div>
                  {pagingForm.passenger_name && (
                    <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 text-xs text-blue-800 leading-relaxed">
                      <p className="font-semibold text-blue-600 mb-1 flex items-center gap-1">
                        <Radio size={11} /> Announcement Preview
                      </p>
                      {buildPagingPreview()}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* ── Right: broadcast targets ── */}
            <div className="px-6 py-5 space-y-5">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Where to Broadcast</p>
                {hasTargets && !isAirportWideSelected && (
                  <button type="button" onClick={() => { setSelectedZones([]); setSelectedGates([]); }}
                    className="text-xs text-red-500 hover:text-red-600 font-medium">
                    Clear all
                  </button>
                )}
              </div>

              {/* Airport Wide quick-select */}
              {airportWideZone && (
                <button type="button" onClick={toggleAirportWide}
                  className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 font-semibold text-sm transition-all ${
                    isAirportWideSelected
                      ? 'border-blue-600 bg-blue-600 text-white shadow-md'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-blue-400 hover:bg-blue-50'
                  }`}
                >
                  <span className={`text-xl ${isAirportWideSelected ? '' : 'grayscale'}`}>🌐</span>
                  <div className="text-left">
                    <p className="font-bold">{airportWideZone.zone_name}</p>
                    <p className={`text-xs font-normal ${isAirportWideSelected ? 'text-blue-200' : 'text-gray-400'}`}>
                      Broadcasts to every zone and gate
                    </p>
                  </div>
                  {isAirportWideSelected && <CheckCircle size={18} className="ml-auto flex-shrink-0" />}
                </button>
              )}

              {!isAirportWideSelected && (
                <>
                  {/* Individual zones */}
                  {otherZones.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                        <Globe size={11} /> Zones
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {otherZones.map(z => {
                          const isSelected = selectedZones.includes(z.zone_code);
                          return (
                            <button key={z.zone_code} type="button" onClick={() => toggleZone(z.zone_code)}
                              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border-2 transition-all ${
                                isSelected
                                  ? 'border-blue-600 bg-blue-600 text-white'
                                  : 'border-gray-200 bg-white text-gray-600 hover:border-blue-400 hover:text-blue-600'
                              }`}
                            >
                              {z.zone_name || z.zone_code}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Gates grouped by terminal */}
                  {terminalLetters.length > 0 && (
                    <div className="space-y-3">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
                        <Radio size={11} /> Gates by Terminal
                      </p>
                      {terminalLetters.map(letter => {
                        const group = gateGroups[letter];
                        const groupIds = group.map(g => String(g.id));
                        const allSelected = groupIds.every(id => selectedGates.includes(id));
                        const someSelected = groupIds.some(id => selectedGates.includes(id));
                        return (
                          <div key={letter} className="bg-gray-50 rounded-xl p-3">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-bold text-gray-600">Terminal {letter}</span>
                              <button type="button" onClick={() => toggleTerminal(letter)}
                                className={`text-xs font-semibold px-2 py-0.5 rounded-lg transition-colors ${
                                  allSelected
                                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                                    : 'bg-white border border-gray-300 text-gray-600 hover:border-blue-400 hover:text-blue-600'
                                }`}
                              >
                                {allSelected ? 'Deselect All' : 'Select All'}
                              </button>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {group.map(g => {
                                const isSelected = selectedGates.includes(String(g.id));
                                return (
                                  <button key={g.id} type="button" onClick={() => toggleGate(g.id)}
                                    className={`px-2 py-1 rounded-lg text-xs font-medium border transition-all ${
                                      isSelected
                                        ? 'border-blue-600 bg-blue-600 text-white'
                                        : 'border-gray-200 bg-white text-gray-600 hover:border-blue-400'
                                    }`}
                                  >
                                    {g.gate_code}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {/* Target summary */}
              {hasTargets && (
                <div className={`rounded-xl px-4 py-3 flex items-center gap-2 text-sm font-medium ${
                  isAirportWideSelected ? 'bg-blue-600 text-white' : 'bg-green-50 border border-green-200 text-green-700'
                }`}>
                  <CheckCircle size={15} className="flex-shrink-0" />
                  {isAirportWideSelected
                    ? 'Broadcasting to the entire airport'
                    : `Broadcasting to ${selectedZones.length > 0 ? `${selectedZones.length} zone${selectedZones.length > 1 ? 's' : ''}` : ''}${selectedZones.length > 0 && selectedGates.length > 0 ? ' + ' : ''}${selectedGates.length > 0 ? `${selectedGates.length} gate${selectedGates.length > 1 ? 's' : ''}` : ''}`
                  }
                </div>
              )}

              {!hasTargets && (
                <div className="bg-gray-50 border border-dashed border-gray-200 rounded-xl px-4 py-3 text-xs text-gray-400 text-center">
                  No targets selected — will save as pending for later approval
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Sticky footer ── */}
        <div className="px-6 py-4 border-t border-gray-100 flex gap-3 flex-shrink-0 bg-white rounded-b-2xl">
          <Button variant="ghost" onClick={onClose} size="lg">
            Cancel
          </Button>
          <div className="flex-1" />
          <Button variant="outline" icon={Send} disabled={submitting} onClick={() => handleSubmit(false)} size="lg">
            Save as Pending
          </Button>
          <Button variant="success" icon={Zap} loading={submitting} disabled={!hasTargets} onClick={() => handleSubmit(true)} size="lg">
            Approve & Broadcast
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Edit Announcement Modal ───────────────────────────────────────────────────

function EditModal({ ann, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: ann.title || '',
    english_text: ann.english_text || '',
    priority: ann.priority || 'normal',
  });
  const [saving, setSaving] = useState(false);

  async function handleSave(e) {
    e.preventDefault();
    if (!form.title.trim() || !form.english_text.trim()) {
      toast.error('Title and text are required'); return;
    }
    setSaving(true);
    try {
      await airportAnnouncementsAPI.edit(ann.id, form);
      toast.success('Announcement updated');
      onSaved();
      onClose();
    } catch (err) {
      if (!err._toasted) toast.error('Failed to save');
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-bold text-gray-900 text-base">Edit Announcement</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
        </div>

        <form onSubmit={handleSave} className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input type="text" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              required className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Announcement Text *</label>
            <textarea value={form.english_text} onChange={e => setForm(f => ({ ...f, english_text: e.target.value }))}
              rows={4} required
              className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
            <div className="flex gap-2">
              {Object.entries(PRIORITY_CFG).map(([val, cfg]) => (
                <button key={val} type="button"
                  onClick={() => setForm(f => ({ ...f, priority: val }))}
                  className={`flex-1 py-2 rounded-xl text-xs font-semibold border transition-colors ${
                    form.priority === val ? 'bg-blue-700 text-white border-blue-700' : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'
                  }`}
                >{cfg.label}</button>
              ))}
            </div>
          </div>
          <div className="flex gap-3 pt-1">
            <Button variant="ghost" onClick={onClose} size="lg" className="flex-1">
              Cancel
            </Button>
            <Button variant="primary" icon={CheckCircle} loading={saving} type="submit" size="lg" className="flex-1">
              {saving ? 'Saving…' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function SupervisorAnnouncementsPage() {
  const [searchParams] = useSearchParams();
  const [pending, setPending] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pending'); // 'pending' | 'history'
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [zones, setZones] = useState([]);
  const [gates, setGates] = useState([]);
  const [selectedZones, setSelectedZones] = useState([]);
  const [selectedGates, setSelectedGates] = useState([]);
  const [scheduleMode, setScheduleMode] = useState('now');
  const [scheduledAt, setScheduledAt] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [rejectNote, setRejectNote] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const loadPending = useCallback(async () => {
    try {
      const res = await airportAnnouncementsAPI.getPending();
      setPending(res.data.data || []);
    } catch { toast.error('Failed to load pending queue'); }
    finally { setLoading(false); }
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await airportAnnouncementsAPI.getAll({ limit: 50 });
      const all = res.data.data || res.data || [];
      setHistory(all.filter(a => !['pending'].includes(a.status)));
    } catch { toast.error('Failed to load history'); }
    finally { setHistoryLoading(false); }
  }, []);

  useEffect(() => {
    loadPending();
    Promise.all([announcementZonesAPI.getAll(), gatesAPI.getAll()])
      .then(([zRes, gRes]) => {
        setZones(zRes.data.data || []);
        setGates(gRes.data.data || []);
      })
      .catch(() => {});
  }, [loadPending]);

  useEffect(() => {
    if (tab === 'history') loadHistory();
  }, [tab, loadHistory]);

  // Deep link support — e.g. /supervisor/announcements?id=123 from the Belt
  // Assignments page, so "view the announcement" links land on the right item.
  useEffect(() => {
    const id = searchParams.get('id');
    if (!id) return;
    airportAnnouncementsAPI.getById(id)
      .then(res => {
        const ann = res.data.data;
        setTab(['pending', 'rejected'].includes(ann.status) ? 'pending' : 'history');
        selectAnnouncement(ann);
      })
      .catch(() => toast.error('Could not load that announcement'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useSupervisorSocket({
    onEvent: (evt) => {
      if (['announcement:submitted', 'announcement:status_change'].includes(evt)) {
        loadPending();
        if (tab === 'history') loadHistory();
      }
    },
  });

  function selectAnnouncement(ann) {
    setSelected(ann);

    // Pre-populate targets from existing announcement targets
    const existingZones = (ann.targets || [])
      .filter(t => t.target_type === 'zone')
      .map(t => t.target_id);
    const existingGates = (ann.targets || [])
      .filter(t => t.target_type === 'gate')
      .map(t => String(t.target_id));

    if (existingZones.length > 0 || existingGates.length > 0) {
      setSelectedZones(existingZones);
      setSelectedGates(existingGates);
    } else if (ann.suggested_gate_id) {
      // Ad-hoc / template requests submitted from a specific gate (AdHocPage,
      // RequestTemplatePage) carry that gate as suggested_gate_id — the approver
      // almost always wants exactly that gate, not Airport Wide, pre-selected.
      setSelectedZones([]);
      setSelectedGates([String(ann.suggested_gate_id)]);
    } else {
      // No gate suggested (e.g. a zone-wide request) — default to Airport Wide
      // if available, otherwise select all zones.
      const airportWide = zones.find(z => z.zone_code === 'ALL' || z.zone_name?.toLowerCase().includes('airport wide') || z.zone_name?.toLowerCase().includes('airport-wide'));
      setSelectedZones(airportWide ? [airportWide.zone_code] : zones.map(z => z.zone_code));
      setSelectedGates([]);
    }

    setScheduleMode('now');
    setScheduledAt('');
    setAdminNotes(ann.admin_notes || '');
    setPreviewUrl(null);
    setShowRejectForm(false);
    setRejectNote('');
  }

  function toggleZone(code) {
    setSelectedZones(prev => prev.includes(code) ? prev.filter(z => z !== code) : [...prev, code]);
  }

  function toggleGate(id) {
    const sid = String(id);
    setSelectedGates(prev => prev.includes(sid) ? prev.filter(g => g !== sid) : [...prev, sid]);
  }

  async function handlePreview() {
    if (!selected) return;
    setPreviewing(true);
    setPreviewUrl(null);
    try {
      const res = await airportAnnouncementsAPI.preview(selected.id);
      setPreviewUrl(res.data.data.audioUrl);
    } catch { toast.error('Preview failed'); }
    finally { setPreviewing(false); }
  }

  async function handleApprove() {
    if (!selected) return;
    const targets = [
      ...selectedZones.map(code => {
        const z = zones.find(z => z.zone_code === code);
        return { target_type: 'zone', target_id: code, target_label: z?.zone_name || code };
      }),
      ...selectedGates.map(gid => {
        const g = gates.find(g => String(g.id) === gid);
        return { target_type: 'gate', target_id: gid, target_label: `Gate ${g?.gate_code || gid}` };
      }),
    ];
    if (targets.length === 0) {
      toast.error('Select at least one target zone or gate'); return;
    }
    setApproving(true);
    try {
      await airportAnnouncementsAPI.approve(selected.id, {
        targets,
        scheduled_at: scheduleMode === 'later' ? scheduledAt : null,
        admin_notes: adminNotes || null,
      });
      toast.success('Approved — audio generating and broadcast queued');
      setSelected(null);
      loadPending();
    } catch (err) {
      if (!err._toasted) toast.error('Approval failed');
    } finally { setApproving(false); }
  }

  async function handleReject() {
    if (!selected) return;
    setRejecting(true);
    try {
      await airportAnnouncementsAPI.reject(selected.id, { admin_notes: rejectNote });
      toast.success('Announcement rejected');
      setSelected(null);
      loadPending();
    } catch (err) {
      if (!err._toasted) toast.error('Rejection failed');
    } finally { setRejecting(false); }
  }

  async function handleCancel(id) {
    if (!window.confirm('Cancel this announcement?')) return;
    try {
      await airportAnnouncementsAPI.cancel(id);
      toast.success('Cancelled');
      loadPending();
      if (tab === 'history') loadHistory();
      if (selected?.id === id) setSelected(null);
    } catch (err) {
      if (!err._toasted) toast.error('Failed to cancel');
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <>
      {showCreateModal && (
        <CreateModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => { loadPending(); setTab('pending'); }}
          zones={zones}
          gates={gates}
        />
      )}
      {editTarget && (
        <EditModal
          ann={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            loadPending();
            if (selected?.id === editTarget.id) {
              // refresh selected item
              airportAnnouncementsAPI.getById(editTarget.id)
                .then(r => setSelected(r.data.data))
                .catch(() => {});
            }
          }}
        />
      )}

      <div className="flex h-[calc(100vh-56px)] overflow-hidden">
        {/* ── Left pane: list ──────────────────────────────────────────────── */}
        <div className="w-72 flex-shrink-0 flex flex-col border-r border-gray-200 bg-white">
          <div className="px-4 pt-4 pb-2 border-b border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <h1 className="text-base font-bold text-gray-900">Announcements</h1>
              <div className="flex gap-1">
                <button onClick={loadPending} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100">
                  <RefreshCw size={14} />
                </button>
                <Button variant="primary" size="sm" icon={Plus} onClick={() => setShowCreateModal(true)}>
                  Create
                </Button>
              </div>
            </div>
            <div className="flex gap-1">
              {['pending', 'history'].map(t => (
                <button key={t} onClick={() => setTab(t)}
                  className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                    tab === t ? 'bg-blue-700 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }`}>
                  {t === 'pending' ? `Pending${pending.length > 0 ? ` (${pending.length})` : ''}` : 'History'}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {tab === 'pending' && (
              pending.length === 0 ? (
                <div className="text-center py-12 px-4">
                  <CheckCircle size={32} className="mx-auto text-green-300 mb-2" />
                  <p className="text-sm text-gray-400">No pending announcements</p>
                </div>
              ) : (
                pending.map(ann => {
                  const pri = PRIORITY_CFG[ann.priority] || PRIORITY_CFG.normal;
                  const isSel = selected?.id === ann.id;
                  return (
                    <button
                      key={ann.id}
                      onClick={() => selectAnnouncement(ann)}
                      className={`w-full text-left px-4 py-3 border-b border-gray-100 transition-colors ${
                        isSel ? 'bg-blue-50 border-l-4 border-l-blue-600' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${pri.dot}`} />
                        <span className="text-sm font-semibold text-gray-900 truncate">{ann.title}</span>
                      </div>
                      <div className="flex items-center gap-2 pl-4 text-xs text-gray-500">
                        <StatusBadge label={pri.label} tone={pri.tone} size="sm" />
                        <span>{timeAgo(ann.createdAt || ann.created_at)}</span>
                      </div>
                      <p className="pl-4 text-xs text-gray-400 mt-0.5 truncate">{ann.requester?.name}</p>
                    </button>
                  );
                })
              )
            )}

            {tab === 'history' && (
              historyLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 size={20} className="animate-spin text-gray-400" />
                </div>
              ) : history.length === 0 ? (
                <div className="text-center py-12 px-4">
                  <p className="text-sm text-gray-400">No history yet</p>
                </div>
              ) : (
                history.map(ann => {
                  const status = STATUS_CFG[ann.status] || STATUS_CFG.pending;
                  const isSel = selected?.id === ann.id;
                  return (
                    <button
                      key={ann.id}
                      onClick={() => selectAnnouncement(ann)}
                      className={`w-full text-left px-4 py-3 border-b border-gray-100 transition-colors ${
                        isSel ? 'bg-blue-50 border-l-4 border-l-blue-600' : 'hover:bg-gray-50'
                      }`}
                    >
                      <p className="text-sm font-semibold text-gray-800 truncate mb-0.5">{ann.title}</p>
                      <div className="flex items-center gap-2 text-xs">
                        <StatusBadge label={status.label} tone={status.tone} pulse={status.pulse} size="sm" />
                        <span className="text-gray-400">{timeAgo(ann.createdAt || ann.created_at)}</span>
                      </div>
                    </button>
                  );
                })
              )
            )}
          </div>
        </div>

        {/* ── Right pane: detail / approval ────────────────────────────────── */}
        {selected ? (
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
            {/* Detail header */}
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-lg font-bold text-gray-900 mb-1">{selected.title}</h2>
                <div className="flex items-center gap-2 flex-wrap text-xs text-gray-500">
                  <StatusBadge
                    label={(PRIORITY_CFG[selected.priority] || PRIORITY_CFG.normal).label}
                    tone={(PRIORITY_CFG[selected.priority] || PRIORITY_CFG.normal).tone}
                  />
                  <StatusBadge
                    label={(STATUS_CFG[selected.status] || STATUS_CFG.pending).label}
                    tone={(STATUS_CFG[selected.status] || STATUS_CFG.pending).tone}
                    pulse={(STATUS_CFG[selected.status] || STATUS_CFG.pending).pulse}
                  />
                  <span className="flex items-center gap-1">
                    <User size={11} /> {selected.requester?.name || '—'}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock size={11} /> {timeAgo(selected.createdAt || selected.created_at)}
                  </span>
                </div>
              </div>
              {selected.status === 'pending' && (
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" icon={Pencil} onClick={() => setEditTarget(selected)}>
                    Edit
                  </Button>
                  <Button variant="dangerLight" size="sm" icon={XCircle} onClick={() => handleCancel(selected.id)}>
                    Cancel
                  </Button>
                </div>
              )}
            </div>

            {/* Text */}
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Announcement Text</p>
              <p className="text-sm text-gray-800 leading-relaxed">{selected.english_text}</p>
            </div>

            {/* Admin notes on rejected/played */}
            {selected.admin_notes && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-1">Notes</p>
                <p className="text-sm text-amber-800">{selected.admin_notes}</p>
              </div>
            )}

            {/* Approver info */}
            {selected.approver && (
              <div className="text-xs text-gray-500">
                Approved by <span className="font-medium text-gray-700">{selected.approver.name}</span>
              </div>
            )}

            {/* Approval section — only for pending */}
            {selected.status === 'pending' && (
              <>
                {/* Preview */}
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">Audio Preview</p>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handlePreview}
                      disabled={previewing}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-sm font-medium text-gray-700 disabled:opacity-50"
                    >
                      {previewing ? <Loader2 size={15} className="animate-spin" /> : <Volume2 size={15} />}
                      Generate Preview
                    </button>
                    {previewUrl && (
                      <audio controls src={previewUrl} className="flex-1 h-9" />
                    )}
                  </div>
                </div>

                {/* Target selection */}
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-3">Broadcast Targets</p>
                  {zones.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                        <Globe size={11} /> Zones
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {zones.map(z => (
                          <button
                            key={z.zone_code}
                            onClick={() => toggleZone(z.zone_code)}
                            className={`px-3 py-1.5 rounded-xl text-xs font-medium border transition-colors ${
                              selectedZones.includes(z.zone_code)
                                ? 'bg-blue-700 text-white border-blue-700'
                                : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                            }`}
                          >
                            {z.zone_name || z.zone_code}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {gates.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                        <Radio size={11} /> Gates
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {gates.map(g => (
                          <button
                            key={g.id}
                            onClick={() => toggleGate(g.id)}
                            className={`px-3 py-1.5 rounded-xl text-xs font-medium border transition-colors ${
                              selectedGates.includes(String(g.id))
                                ? 'bg-blue-700 text-white border-blue-700'
                                : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                            }`}
                          >
                            {g.gate_code}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Schedule — a request pinned to a queue position (operator's
                    "Insert here" on the queue page) keeps that position; the
                    backend falls back to the request's own sequence timing, so
                    Now/Later don't apply and are hidden to avoid confusion. */}
                {selected?.timing_type === 'sequence' ? (
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">Schedule</p>
                  <div className="rounded-xl bg-purple-50 border border-purple-200 px-3 py-2.5 text-sm text-purple-800">
                    <span className="font-semibold">Requested queue position:</span>{' '}
                    {selected.insert_after_id ? 'after a specific announcement' : 'top of the queue (plays next)'} — it will be inserted there when approved.
                  </div>
                </div>
                ) : (
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">Schedule</p>
                  <div className="flex gap-2 mb-2">
                    {['now', 'later'].map(m => (
                      <button
                        key={m}
                        onClick={() => setScheduleMode(m)}
                        className={`flex-1 py-2 rounded-xl text-sm font-semibold border transition-colors ${
                          scheduleMode === m
                            ? 'bg-blue-700 text-white border-blue-700'
                            : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'
                        }`}
                      >
                        {m === 'now' ? <span className="flex items-center justify-center gap-1"><Zap size={13} /> Broadcast Now</span> : 'Schedule Later'}
                      </button>
                    ))}
                  </div>
                  {scheduleMode === 'later' && (
                    <input
                      type="datetime-local"
                      value={scheduledAt}
                      onChange={e => setScheduledAt(e.target.value)}
                      className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  )}
                </div>
                )}

                {/* Approval notes */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Approval Notes</label>
                  <textarea
                    value={adminNotes}
                    onChange={e => setAdminNotes(e.target.value)}
                    placeholder="Optional notes..."
                    rows={2}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  />
                </div>

                {/* Action buttons */}
                {!showRejectForm ? (
                  <div className="flex gap-3">
                    <Button variant="success" icon={CheckCircle} loading={approving} onClick={handleApprove} size="lg" className="flex-1">
                      Approve & Broadcast
                    </Button>
                    <Button variant="dangerLight" icon={XCircle} onClick={() => setShowRejectForm(true)} size="lg">
                      Reject
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3 border border-red-200 rounded-xl p-4 bg-red-50">
                    <p className="text-sm font-semibold text-red-700">Reject Announcement</p>
                    <textarea
                      value={rejectNote}
                      onChange={e => setRejectNote(e.target.value)}
                      placeholder="Reason for rejection..."
                      rows={2}
                      className="w-full border border-red-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 resize-none bg-white"
                    />
                    <div className="flex gap-2">
                      <Button variant="danger" icon={XCircle} loading={rejecting} onClick={handleReject} size="lg" className="flex-1">
                        Confirm Reject
                      </Button>
                      <Button variant="ghost" onClick={() => setShowRejectForm(false)} size="lg">
                        Back
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center text-center px-8">
            <div>
              <Megaphone size={48} className="mx-auto text-gray-200 mb-4" />
              <p className="text-gray-400 font-medium">Select an announcement to review</p>
              <p className="text-sm text-gray-300 mt-1">Or create a new one with the button above</p>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
